/* eslint-disable react/display-name */
/* eslint-disable no-unused-vars */
import { useCallback, useMemo } from 'react';
import { Button, Tag, Avatar, Tooltip } from 'antd';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  PhoneOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import AdvancedTable from '../../components/AdvancedTable';
import { GetAllCancelledOrderApi } from '../../utils/api';
import moment from 'moment';
import { getStatus } from '../../utils/functions';
import { calculateBilling, formatCurrency } from '../../utils/billingUtils';
import { PrakurtiCirculerLogo } from '../../components/imagepath';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';

const CancelledOrders = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector(state => state?.auth);

  // 1. Column Definitions
  const columns = useMemo(() => [
    {
      id: 'sr',
      name: '#',
      width: 50,
      cell: (row, index) => (
        <span style={{ color: '#888' }}>{index + 1}</span>
      ),
    },
    {
      id: 'customer',
      name: 'Customer',
      selector: row => row?.customerId?.name,
      minWidth: 200,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Link
            to={`/order-history/order-history-overview/${row?.id || row?._id}`}
            style={{ fontWeight: 600, color: '#1890ff', fontSize: '14px' }}
          >
            {row?.name || row?.customerId?.name || row?.additionalInfo?.name || '--'}
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}>
            <PhoneOutlined style={{ fontSize: '11px' }} />
            {row?.additionalInfo?.phoneNo || row?.customerId?.phoneNo || '--'}
          </div>
          {(row?.customerId?.email || row?.additionalInfo?.email) && (
            <div style={{ fontSize: '11px', color: '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>
              {row?.customerId?.email || row?.additionalInfo?.email}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'bookingDetails',
      name: 'Booking Details',
      selector: row => row?.bookingFor,
      minWidth: 250,
      cell: row => {
        const info = row?.additionalInfo || {};
        const startDate = info?.startDate;
        const endDate = info?.endDate;
        const bookingDate = info?.bookingDate;
        const startTime = info?.startTime;

        let specifics = info?.serviceName;
        if (!specifics && info?.roomDetails?.length > 0) {
          specifics = info.roomDetails.map(r => r.roomTypeName).join(', ');
        }
        if (!specifics && info?.treatmentData?.length > 0) {
          specifics = info.treatmentData.map(t => t.treatmentName).join(', ');
        }
        if (!specifics && info?.serviceData?.length > 0) {
          specifics = info.serviceData.map(s => s.name).join(', ');
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 600, color: '#E67E22', fontSize: '13px' }}>
                {row?.bookingFor || '--'}
              </span>
              {row?.isSimulation && (
                <Tooltip title={`Created By: ${info?.createdByName || row?.createdByName || 'System'}`}>
                  <Avatar src={PrakurtiCirculerLogo} size={16} />
                </Tooltip>
              )}
            </div>

            <div style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CalendarOutlined style={{ fontSize: '11px' }} />
              {startDate && endDate ? (
                <span>{startDate} <span style={{ color: '#aaa' }}>to</span> {endDate}</span>
              ) : (
                <span>{bookingDate || '--'} {startTime && `at ${startTime}`}</span>
              )}
            </div>

            {specifics && (
              <div style={{ fontSize: '11px', color: '#7f8c8d', fontStyle: 'italic', lineHeight: '1.2' }}>
                {specifics}
              </div>
            )}
          </div>
        )
      },
    },
    {
      id: 'cancellation',
      name: 'Cancellation Info',
      minWidth: 200,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{ fontSize: '12px', color: '#e74c3c', fontWeight: 600 }}>
            <InfoCircleOutlined style={{ marginRight: '4px' }} />
            {row?.cancelReason || 'No reason provided'}
          </div>
          <div style={{ fontSize: '11px', color: '#7f8c8d', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <ClockCircleOutlined style={{ fontSize: '10px' }} />
            {row?.cancelledAt ? moment(row.cancelledAt).format('DD MMM, hh:mm A') : '--'}
          </div>
        </div>
      ),
    },
    {
      id: 'financials',
      name: 'Billing',
      selector: row => row?.amount,
      width: 160,
      cell: row => {
        const billing = calculateBilling(row);
        if (!billing) return '--';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', fontWeight: 600 }}>
              <Tooltip title="Total Gross Amount">
                <span style={{ color: '#7f8c8d', textDecoration: billing.discountValue > 0 ? 'line-through' : 'none', fontSize: '11px' }}>
                  {formatCurrency(billing.total)}
                </span>
              </Tooltip>
              {billing.discountRate > 0 && (
                <span style={{ color: '#27ae60', fontSize: '10px' }}>
                  -{billing.discountType === 'Percentage' ? `${billing.discountRate}%` : formatCurrency(billing.discountRate)}
                </span>
              )}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#2c3e50' }}>
              {formatCurrency(billing.netAmount)}
            </div>
            <div style={{ color: '#27ae60', fontSize: '11px', fontWeight: 600 }}>
              Refunded / Settled
            </div>
          </div>
        );
      },
    },
    {
      id: 'status',
      name: 'Status',
      width: 140,
      cell: row => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Tag color={getStatus(row?.paymentStatus || 'Failed')} style={{ margin: 0, textAlign: 'center', fontSize: '11px', width: '100%' }}>
            Pay: {row?.paymentStatus || 'Failed'}
          </Tag>
          <Tag color={getStatus(row?.orderStatus || 'Cancelled')} style={{ margin: 0, textAlign: 'center', fontSize: '11px', width: '100%' }}>
            Ord: {row?.orderStatus || 'Cancelled'}
          </Tag>
        </div>
      )
    },
    {
      id: 'actions',
      name: 'Action',
      width: 100,
      cell: row => (
        <Link to={`/order-history/order-history-overview/${row?.id || row?._id}`}>
          <Button type="primary" size="small" ghost>
            View
          </Button>
        </Link>
      ),
    },
  ], []);

  // 2. Filter Configuration
  const filterConfig = useMemo(() => [
    {
      id: 'customerName',
      label: 'Customer Name',
      type: 'text',
      placeholder: 'Search name...',
    },
    {
      id: 'bookingFor',
      label: 'Booking Type',
      type: 'select',
      options: [
        { label: 'Treatment', value: 'Treatment' },
        { label: 'Consultation', value: 'Consultation' },
        { label: 'Detox', value: 'Detox' },
        { label: 'Service', value: 'Service' },
      ],
    },
    {
      id: 'cancelledDateRange',
      label: 'Cancellation Date',
      type: 'dateRange',
      placeholder: 'Select Date Range',
      quickFilters: ['today', 'yesterday', 'last7days', 'last30days'],
    }
  ], []);

  // 3. Data Fetching
  const fetchData = useCallback(async ({ page, perPage, ...filters }) => {
    const queryParams = new URLSearchParams();
    queryParams.append('page', page);
    queryParams.append('limit', perPage);

    if (filters.customerName) queryParams.append('customerName', filters.customerName);
    if (filters.bookingFor) queryParams.append('bookingFor', filters.bookingFor);

    if (filters.cancelledDateRange_start) {
      queryParams.append('cancelledOrderStartDate', moment(filters.cancelledDateRange_start).format('YYYY-MM-DD'));
    }
    if (filters.cancelledDateRange_end) {
      queryParams.append('cancelledOrderEndDate', moment(filters.cancelledDateRange_end).format('YYYY-MM-DD'));
    }

    try {
      const response = await GetAllCancelledOrderApi({ Query: `?${queryParams.toString()}` });
      if (response?.status === 200) {
        const data = response?.data?.data?.[0];
        return {
          data: data?.docs || [],
          total: data?.totalDocs || 0,
        };
      }
      return { data: [], total: 0 };
    } catch (error) {
      console.error('Fetch Error:', error);
      return { data: [], total: 0 };
    }
  }, []);

  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontWeight: 700, color: '#2c3e50', margin: 0 }}>Cancelled Orders</h3>
              <small style={{ color: '#7f8c8d' }}>View and manage cancelled bookings and refunds</small>
            </div>
          </div>

          <AdvancedTable
            resource="CancelledOrders"
            columns={columns}
            fetchData={fetchData}
            filterConfig={filterConfig}
            enableSearch={false}
            enableFilters={true}
            enableExport={true}
            enableRefresh={true}
            exportFileName="Cancelled_Orders_Report"
          />
        </div>
      </div>
    </>
  );
};

export default CancelledOrders;
